import type {ImageContentKind, SniffedImageFormat, UploadImageMimeType} from './types';

/** Claude's native tile edge; plenty for meal photos and label OCR. Features may pass their own. */
export const LLM_IMAGE_MAX_LONG_EDGE_PX = 1568;

/** Photos are resized, not squeezed: q90 keeps text edges and colour clean. */
export const PHOTO_JPEG_QUALITY = 0.9;

export const MEBIBYTE = 1024 * 1024;

/** Refused before a single byte is read; covers every phone's default capture mode. */
export const MAX_INPUT_BYTES = 32 * MEBIBYTE;

/**
 * Mirrors `MAX_DECODE_PIXELS` in `apps/server/src/infrastructure/image/image-limits.ts`.
 * Enforced from the header before decoding so a decompression bomb never allocates.
 */
export const MAX_DECODE_MEGAPIXELS = 50;
export const MAX_DECODE_PIXELS = MAX_DECODE_MEGAPIXELS * 1_000_000;

/**
 * Initial slice read for sniffing, header dimensions, and EXIF. A JPEG whose header
 * segments run past it is re-read in growing windows until its scan data is reached;
 * a JPEG is never decoded without a frame header.
 */
export const HEADER_SCAN_BYTES = MEBIBYTE;

export const CLASSIFIER_TILE_SIZE_PX = 32;
export const CLASSIFIER_GRID_SIZE = 4;

/**
 * The source format sets a prior; pixel statistics only flip it on strong evidence.
 * `flatRatio` is the share of pixels identical to their right neighbour, `uniqueRatio`
 * the distinct colours per sampled pixel. Screenshots are flat with few colours;
 * photos are noisy with many.
 */
export const KIND_DECISION_BY_PRIOR = {
  photo: {graphicIfFlatRatioAtLeast: 0.6, andUniqueRatioAtMost: 0.1},
  graphic: {photoIfFlatRatioAtMost: 0.1, andUniqueRatioAtLeast: 0.4},
} as const;

/** WebP is decided by its flavour (lossless → graphic, lossy → photo). */
export const PRIOR_BY_FORMAT: Readonly<
  Record<Exclude<SniffedImageFormat, 'webp'>, ImageContentKind>
> = {
  jpeg: 'photo',
  heic: 'photo',
  avif: 'photo',
  png: 'graphic',
  gif: 'graphic',
  bmp: 'graphic',
};

export const UPLOAD_EXTENSION_BY_MIME: Readonly<Record<UploadImageMimeType, string>> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

/** Pickers only hand over HEIC when it is listed; Chrome reports an empty type for `.heic`, hence the extensions. */
export const HEIC_INPUT_ACCEPT = ['image/heic', 'image/heif', '.heic', '.heif'] as const;
